// Feedback API Entity
// Handles all feedback-related database operations

import { supabase, handleSupabaseError, getCurrentUser } from '@/lib/supabase'
import { FeedbackEmailService } from '@/services/FeedbackEmailService'

export const Feedback = {
  // Get all feedback (admin only)
  async getAll(limit = 50, offset = 0, filters = {}) {
    try {
      let query = supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.feedback_type && filters.feedback_type !== 'all') {
        query = query.eq('feedback_type', filters.feedback_type);
      }
      if (filters.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }
      if (filters.user_email) {
        query = query.ilike('user_email', `%${filters.user_email}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Get feedback by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Get user's own feedback
  async getUserFeedback(userId = null) {
    try {
      const user = userId || await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Create new feedback
  async create(feedbackData) {
    try {
      let user = null;
      let userEmail = null;
      let userProfileId = null;
      
      try {
        // Add timeout for user lookup to prevent hanging (5 seconds)
        const userLookupTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('User lookup timed out')), 5000);
        });
        
        user = await Promise.race([
          getCurrentUser(),
          userLookupTimeout
        ]);
        userEmail = user?.email || feedbackData.user_email || null;
        
        // Try to get the profile ID if it exists
        // With RLS policies in place, we can now safely check for the profile
        if (user?.id) {
          try {
            // Add timeout for profile lookup (3 seconds)
            const profileLookupTimeout = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Profile lookup timed out')), 3000);
            });
            
            const profileQuery = supabase
              .from('profiles')
              .select('id')
              .eq('id', user.id)
              .single();
            
            const profileResult = await Promise.race([
              profileQuery,
              profileLookupTimeout
            ]);
            
            const { data: profile, error: profileError } = profileResult;
            
            if (profile && !profileError) {
              userProfileId = profile.id;
            } else if (profileError && profileError.code !== 'PGRST116') {
              // PGRST116 is "not found" which is fine, but log other errors
              console.warn('Error checking profile for feedback:', profileError);
            }
          } catch (profileTimeoutError) {
            // Profile lookup timed out, continue without profile ID
            console.warn('Profile lookup timed out, continuing without profile ID');
          }
        }
      } catch (userError) {
        // If user lookup fails, continue without user info
        // This allows feedback submission even if there are auth issues
        console.warn('Could not get user for feedback:', userError);
        userEmail = feedbackData.user_email || null;
      }
      
      // Prepare feedback data
      // user_id is set if profile exists, otherwise null
      // The RLS policies now allow authenticated users to insert feedback
      const newFeedback = {
        user_id: userProfileId || null,
        user_email: userEmail,
        feedback_type: feedbackData.feedback_type,
        title: feedbackData.title || null,
        description: feedbackData.description,
        page_url: feedbackData.page_url || window.location.href,
        user_agent: navigator.userAgent,
        browser_info: {
          platform: navigator.platform,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          screenResolution: `${screen.width}x${screen.height}`,
          viewportSize: `${window.innerWidth}x${window.innerHeight}`
        },
        status: 'new',
        priority: this._determinePriority(feedbackData.feedback_type, feedbackData.description)
      };

      const { data, error } = await supabase
        .from('feedback')
        .insert([newFeedback])
        .select()
        .single();

      if (error) {
        console.error('Feedback insert error:', error);
        throw handleSupabaseError(error);
      }

      // If insert succeeded but select returned no data (RLS issue), create a minimal response
      if (!data) {
        console.warn('Feedback inserted but select returned no data (possible RLS issue)');
        // Return a minimal success object so the UI can proceed
        // The feedback was inserted, even if we can't read it back
        return {
          id: 'unknown',
          ...newFeedback,
          created_at: new Date().toISOString()
        };
      }

      // Send email notifications (don't wait for them to complete)
      // Note: Email functionality requires send-email Edge Function to be deployed
      FeedbackEmailService.sendFeedbackNotification(data).catch(err => 
        console.warn('Email notification not available (Edge Function not deployed):', err.message)
      );
      
      FeedbackEmailService.sendUserConfirmation(data).catch(err => 
        console.warn('Email confirmation not available (Edge Function not deployed):', err.message)
      );

      return data;
    } catch (error) {
      console.error('Error creating feedback:', error);
      // Ensure error is properly thrown with formatted message
      const formattedError = error instanceof Error ? error : new Error(String(error));
      throw handleSupabaseError(formattedError);
    }
  },

  // Update feedback (admin only)
  async update(id, updates) {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      // Get current feedback to check for status changes
      const currentFeedback = await this.getById(id);
      const oldStatus = currentFeedback.status;

      // Add admin user ID to updates
      const updateData = {
        ...updates,
        admin_user_id: user.id,
        updated_at: new Date().toISOString()
      };

      // If status is being changed to resolved, set resolved_at
      if (updates.status === 'resolved' && !updates.resolved_at) {
        updateData.resolved_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('feedback')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Send status update notification if status changed
      if (updates.status && updates.status !== oldStatus) {
        FeedbackEmailService.sendStatusUpdateNotification(data, oldStatus, updates.status).catch(err => 
          console.warn('Status update email not available (Edge Function not deployed):', err.message)
        );
      }

      return data;
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Delete feedback (admin only)
  async delete(id) {
    try {
      const { error } = await supabase
        .from('feedback')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Get feedback statistics
  async getStats() {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('feedback_type, status, priority, created_at');

      if (error) throw error;

      const stats = {
        total: data.length,
        byType: {},
        byStatus: {},
        byPriority: {},
        recent: data.filter(f => new Date(f.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length,
        thisMonth: data.filter(f => new Date(f.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length
      };

      // Count by type
      data.forEach(f => {
        stats.byType[f.feedback_type] = (stats.byType[f.feedback_type] || 0) + 1;
        stats.byStatus[f.status] = (stats.byStatus[f.status] || 0) + 1;
        stats.byPriority[f.priority] = (stats.byPriority[f.priority] || 0) + 1;
      });

      return stats;
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Search feedback
  async search(searchTerm, filters = {}) {
    try {
      let query = supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      // Text search in description and title
      if (searchTerm) {
        query = query.or(`description.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%`);
      }

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.feedback_type && filters.feedback_type !== 'all') {
        query = query.eq('feedback_type', filters.feedback_type);
      }
      if (filters.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Helper function to determine priority based on feedback type and content
  _determinePriority(feedbackType, description) {
    const urgentKeywords = ['crash', 'broken', 'not working', 'error', 'bug', 'urgent', 'critical'];
    const highKeywords = ['issue', 'problem', 'slow', 'performance', 'login', 'signup'];
    
    const descriptionLower = description.toLowerCase();
    
    if (urgentKeywords.some(keyword => descriptionLower.includes(keyword))) {
      return 'urgent';
    }
    
    if (feedbackType === 'bug' || highKeywords.some(keyword => descriptionLower.includes(keyword))) {
      return 'high';
    }
    
    if (feedbackType === 'feature_request') {
      return 'medium';
    }
    
    return 'low';
  },

  // Mark feedback as resolved
  async markResolved(id, adminNotes = null) {
    try {
      return await this.update(id, {
        status: 'resolved',
        admin_notes: adminNotes,
        resolved_at: new Date().toISOString()
      });
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Get feedback by status
  async getByStatus(status) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  },

  // Get recent feedback
  async getRecent(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  }
};
